/**
 * Security utility functions for the Superior Payroll application
 */

// Sanitize user input to prevent XSS attacks.
// NOTE: This HTML-ENCODES the input. Only use it for values that will be
// injected into a raw-HTML context. Do NOT use it on data you persist and then
// render through React — React already escapes on render, so encoding here
// double-escapes and corrupts the stored value (e.g. "O'Brien" → "O&#x27;Brien").
// For stored text, use sanitizeText() instead.
export const sanitizeInput = (input: string): string => {
  if (!input) return '';

  // Replace potentially dangerous characters
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Sanitize text for STORAGE: strip angle brackets and control characters and
// trim, but do NOT HTML-encode. React escapes on render, so stored values stay
// human-readable (apostrophes, slashes, ampersands survive intact).
export const sanitizeText = (input: string): string => {
  if (!input) return '';
  return input
    .replace(/[<>]/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim();
};

// Decode the HTML entities that the legacy sanitizeInput() wrote into stored
// data, so older records (e.g. "Vibin&#x27; LLC") display correctly. Reverse
// order of sanitizeInput so "&amp;" is unescaped last.
export const decodeHtmlEntities = (input: string): string => {
  if (!input) return '';
  return input
    .replace(/&#x2F;/gi, '/')
    .replace(/&#x27;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
};

// Validate email format
export const isEmailValid = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// Password strength checker
export const getPasswordStrength = (password: string): number => {
  if (!password) return 0;
  
  let strength = 0;
  
  // Length check
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 10;
  
  // Character variety checks
  if (/[a-z]/.test(password)) strength += 10; // lowercase
  if (/[A-Z]/.test(password)) strength += 20; // uppercase
  if (/[0-9]/.test(password)) strength += 20; // numbers
  if (/[^a-zA-Z0-9]/.test(password)) strength += 20; // special characters
  
  return Math.min(100, strength);
};

// Rate limiting utility
export class RateLimiter {
  private attempts: Record<string, number[]> = {};
  private readonly maxAttempts: number;
  private readonly timeWindow: number;

  constructor(maxAttempts = 5, timeWindow = 60000) {
    this.maxAttempts = maxAttempts;
    this.timeWindow = timeWindow; // in milliseconds
  }

  // Check if the key (e.g., IP address, user ID) has reached its limit
  public isRateLimited(key: string): boolean {
    const now = Date.now();
    
    // Initialize attempts array if it doesn't exist
    if (!this.attempts[key]) {
      this.attempts[key] = [];
    }
    
    // Filter out attempts older than the time window
    this.attempts[key] = this.attempts[key].filter(timestamp => now - timestamp < this.timeWindow);
    
    // Check if number of attempts exceeds the maximum
    return this.attempts[key].length >= this.maxAttempts;
  }

  // Record a new attempt
  public addAttempt(key: string): void {
    const now = Date.now();
    
    if (!this.attempts[key]) {
      this.attempts[key] = [];
    }
    
    this.attempts[key].push(now);
  }

  // Get remaining attempts
  public getRemainingAttempts(key: string): number {
    if (!this.attempts[key]) return this.maxAttempts;
    
    // Filter out attempts older than the time window
    const now = Date.now();
    this.attempts[key] = this.attempts[key].filter(timestamp => now - timestamp < this.timeWindow);
    
    return Math.max(0, this.maxAttempts - this.attempts[key].length);
  }

  // Clear attempts for a key
  public clearAttempts(key: string): void {
    delete this.attempts[key];
  }
}

// Content Security Policy (CSP) helper
export const setupCSP = (): void => {
  if (process.env.REACT_APP_CSP_ENABLED !== 'true') return;
  
  // Create a meta tag for CSP
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = [
    // Default directives
    "default-src 'self'",
    
    // Script sources
    "script-src 'self' https://www.google.com https://www.gstatic.com https://www.googletagmanager.com https://*.firebaseio.com https://*.firebaseapp.com",
    
    // Style sources
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    
    // Font sources
    "font-src 'self' https://fonts.gstatic.com",
    
    // Image sources
    "img-src 'self' data: https:",
    
    // Connect sources - Adding identitytoolkit.googleapis.com domain for Firebase Authentication
    "connect-src 'self' https://*.firebaseio.com https://*.firebaseapp.com https://www.googleapis.com https://identitytoolkit.googleapis.com https://securetoken.googleapis.com https://firestore.googleapis.com",
    
    // Frame sources
    "frame-src 'self' https://www.google.com",
    
    // Upgrade insecure requests
    "upgrade-insecure-requests"
  ].join('; ');
  
  // Add meta tag to head
  document.head.appendChild(meta);
};

// Session timeout management
export class SessionManager {
  private timeoutId: number | null = null;
  private warningTimeoutId: number | null = null;
  private absoluteTimeoutId: number | null = null;
  private readonly timeoutDuration: number;
  private readonly warningDuration: number;
  private readonly absoluteTimeoutDuration: number = 28800000; // 8 hours absolute cap (cannot be reset by activity)
  private onTimeout: () => void;
  private onWarning: () => void;
  private sessionStartTime: number;

  constructor(timeoutDuration = 30 * 60 * 1000, warningDuration = 5 * 60 * 1000, onTimeout: () => void, onWarning: () => void) {
    this.timeoutDuration = timeoutDuration; // 30 minutes by default
    this.warningDuration = warningDuration; // 5 minutes warning before timeout
    this.onTimeout = onTimeout;
    this.onWarning = onWarning;
    this.sessionStartTime = Date.now();

    // Set up event listeners for user activity
    this.setupActivityTracking();

    // Set absolute timeout that cannot be reset by activity
    this.absoluteTimeoutId = window.setTimeout(() => {
      this.onTimeout();
    }, this.absoluteTimeoutDuration);
  }

  // Start session timer
  public startTimer(): void {
    this.resetTimer();
  }

  // Reset timer on user activity
  public resetTimer(): void {
    // Check if absolute timeout has been reached
    const elapsed = Date.now() - this.sessionStartTime;
    if (elapsed >= this.absoluteTimeoutDuration) {
      this.onTimeout();
      return;
    }

    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
    }

    if (this.warningTimeoutId) {
      window.clearTimeout(this.warningTimeoutId);
    }

    // Calculate remaining time before absolute timeout
    const remainingAbsolute = this.absoluteTimeoutDuration - elapsed;

    // Use the lesser of the inactivity timeout or remaining absolute time
    const effectiveTimeout = Math.min(this.timeoutDuration, remainingAbsolute);

    // Set timeout for warning
    const warningTime = effectiveTimeout - this.warningDuration;
    if (warningTime > 0) {
      this.warningTimeoutId = window.setTimeout(() => {
        this.onWarning();
      }, warningTime);
    }

    // Set timeout for session expiration
    this.timeoutId = window.setTimeout(() => {
      this.onTimeout();
    }, effectiveTimeout);
  }

  // Stop tracking session
  public stopTimer(): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      window.clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    if (this.absoluteTimeoutId) {
      window.clearTimeout(this.absoluteTimeoutId);
      this.absoluteTimeoutId = null;
    }
  }

  // Track user activity to reset timer
  private setupActivityTracking(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    events.forEach(event => {
      document.addEventListener(event, () => {
        this.resetTimer();
      });
    });
  }
}

// Generate a secure random token
export const generateSecureToken = (length = 32): string => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// URL sanitization (prevent open redirects)
export const sanitizeRedirectUrl = (url: string, allowedDomains: string[] = []): string => {
  if (!url) return '/';
  
  try {
    // If it's a relative URL, it's safe
    if (url.startsWith('/')) return url;
    
    // Check if the URL is for an allowed domain
    const urlObj = new URL(url);
    
    if (allowedDomains.includes(urlObj.hostname)) {
      return url;
    }
    
    // Default to home page if not allowed
    return '/';
  } catch (error) {
    // If URL is invalid, default to home page
    return '/';
  }
};

/**
 * API Key rotation utility to enhance security
 * Reminds administrators when API keys should be rotated and helps implement rotation schedules
 */
export class ApiKeyManager {
  private readonly rotationIntervalDays: number;
  private readonly warningDays: number;
  private readonly storageKeyPrefix: string;
  
  constructor(rotationIntervalDays = 90, warningDays = 14, storageKeyPrefix = 'api_key_') {
    this.rotationIntervalDays = rotationIntervalDays;
    this.warningDays = warningDays;
    this.storageKeyPrefix = storageKeyPrefix;
  }
  
  /**
   * Register an API key for rotation tracking
   * @param keyName Name/identifier for the API key
   * @param createdDate When the key was created/last rotated
   */
  public registerKey(keyName: string, createdDate: Date = new Date()): void {
    localStorage.setItem(
      `${this.storageKeyPrefix}${keyName}`,
      createdDate.toISOString()
    );
  }
  
  /**
   * Check if an API key is due for rotation
   * @param keyName Name/identifier for the API key
   * @returns Object with status and days left info
   */
  public checkRotationStatus(keyName: string): {
    needsRotation: boolean;
    warningPeriod: boolean;
    daysLeft: number;
    daysSinceCreation: number;
  } {
    const storedDate = localStorage.getItem(`${this.storageKeyPrefix}${keyName}`);
    if (!storedDate) {
      return {
        needsRotation: false,
        warningPeriod: false,
        daysLeft: this.rotationIntervalDays,
        daysSinceCreation: 0
      };
    }
    
    const createdDate = new Date(storedDate);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysLeft = this.rotationIntervalDays - daysDiff;
    
    return {
      needsRotation: daysLeft <= 0,
      warningPeriod: daysLeft <= this.warningDays && daysLeft > 0,
      daysLeft: Math.max(0, daysLeft),
      daysSinceCreation: daysDiff
    };
  }
  
  /**
   * Record that an API key has been rotated
   * @param keyName Name/identifier for the API key
   */
  public rotateKey(keyName: string): void {
    this.registerKey(keyName, new Date());
  }
  
  /**
   * Get a list of all registered API keys and their status
   */
  public getAllKeysStatus(): Array<{
    keyName: string;
    status: ReturnType<ApiKeyManager['checkRotationStatus']>;
  }> {
    const result = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.storageKeyPrefix)) {
        const keyName = key.substring(this.storageKeyPrefix.length);
        result.push({
          keyName,
          status: this.checkRotationStatus(keyName)
        });
      }
    }
    
    return result;
  }
} 